import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { HardDrive, Database, Package, Cog, Globe, FileText, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const AdminStorage = () => {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStorageStats();
  }, []);

  const fetchStorageStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/storage-stats');
      setStorageData(response.data);
    } catch (error) {
      toast.error('Depolama bilgileri yüklenemedi');
      console.error('Storage stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (key) => {
    const icons = {
      mongodb: <Database className="h-4 w-4" />,
      frontend: <Globe className="h-4 w-4" />,
      backend: <Package className="h-4 w-4" />,
      system: <Cog className="h-4 w-4" />,
      playwright: <HardDrive className="h-4 w-4" />,
      logs: <FileText className="h-4 w-4" />
    };
    return icons[key] || <HardDrive className="h-4 w-4" />;
  };

  const getRecommendationColor = (type) => {
    const colors = {
      success: 'bg-green-500/10 text-green-400 border-green-500/30',
      info: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      warning: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      error: 'bg-red-500/10 text-red-400 border-red-500/30'
    };
    return colors[type] || colors.info;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark py-8">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center py-12">
            <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!storageData) {
    return (
      <div className="min-h-screen bg-brand-dark py-8">
        <div className="container mx-auto px-6 max-w-7xl">
          <Card className="border-red-500/40 bg-red-900/10">
            <CardContent className="py-8 text-center">
              <p className="text-red-400">Depolama bilgileri yüklenemedi</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { disk, categories, recommendations } = storageData;

  return (
    <div className="min-h-screen bg-brand-dark py-6">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white mb-1">Depolama Yönetimi</h1>
            <p className="text-sm text-slate-400">Disk kullanımını takip edin</p>
          </div>
          <Button onClick={fetchStorageStats} variant="outline" size="sm" className="text-white border-white/10">
            <RefreshCw className="h-3 w-3 mr-2" />
            Yenile
          </Button>
        </div>

        {/* Main Disk Usage */}
        <Card className="border-white/10 bg-slate-900/50 backdrop-blur mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center space-x-2">
              <HardDrive className="h-4 w-4 text-primary" />
              <span>Toplam Disk Kullanımı</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="text-2xl font-bold text-white">{disk.used}GB / {disk.total}GB</p>
                  <p className="text-xs text-slate-400">{disk.available}GB boş alan</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-primary">%{disk.use_percent}</p>
                  <p className="text-xs text-slate-400">Kullanım</p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="space-y-1">
                <Progress value={disk.use_percent} className="h-2" />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>0 GB</span>
                  <span>{disk.total} GB</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <Card className="border-white/10 bg-slate-900/50 backdrop-blur mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white">Öneriler</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {recommendations.map((rec, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg border ${getRecommendationColor(rec.type)}`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{rec.icon}</span>
                      <p className="text-xs flex-1">{rec.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Breakdown */}
        <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-white">Kategori Dağılımı</CardTitle>
            <CardDescription className="text-xs text-slate-400">Depolama alanının kategori bazında kullanımı</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(categories).map(([key, data]) => (
                <Card key={key} className="border-white/10 bg-slate-800/50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div
                          className="p-1.5 rounded-lg flex-shrink-0"
                          style={{ backgroundColor: `${data.color}20` }}
                        >
                          <div style={{ color: data.color }} className="w-4 h-4">
                            {getCategoryIcon(key)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium truncate">{data.description}</p>
                          <p className="text-xs text-slate-500 truncate">{data.path}</p>
                        </div>
                      </div>
                      <Badge
                        className="text-xs text-white border-white/20 ml-2 flex-shrink-0"
                        style={{ backgroundColor: `${data.color}30` }}
                      >
                        {data.size}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminStorage;
